import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { businessDomainOptions } from '../../config/businessDomains';

export interface AgentPromptFormData {
  id?: string;
  agentConfigId: string;
  businessDomain: string;
  systemRole: string;
  mainTask: string;
  instructions: string;
  restrictions: string;
  responseFormatRules: string;
  promptInput: string;
  contextVariables: string;
  fewShotExamples: string;
  isActive: boolean;
}

interface CreateEditAgentPromptModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (formData: AgentPromptFormData) => void;
  mode: 'create' | 'edit';
  saving: boolean;
  saveError: string | null;
  agentConfigId: string;
  initialData: AgentPromptFormData | null;
}

const jsonEditorSx = {
  '& .MuiInputBase-root': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
  },
  '& .MuiInputBase-input': {
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '0.85rem',
  },
};

const CreateEditAgentPromptModal: React.FC<CreateEditAgentPromptModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving,
  saveError,
  agentConfigId,
  initialData,
}) => {
  const [formData, setFormData] = useState<AgentPromptFormData>({
    agentConfigId: '',
    businessDomain: 'general',
    systemRole: '',
    mainTask: '',
    instructions: '',
    restrictions: '',
    responseFormatRules: '',
    promptInput: '',
    contextVariables: '',
    fewShotExamples: '',
    isActive: true,
  });

  const [contextVarsError, setContextVarsError] = useState<string | null>(null);
  const [fewShotError, setFewShotError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        agentConfigId,
        businessDomain: 'general',
        systemRole: '',
        mainTask: '',
        instructions: '',
        restrictions: '',
        responseFormatRules: '',
        promptInput: '',
        contextVariables: '',
        fewShotExamples: '',
        isActive: true,
      });
    }
    setContextVarsError(null);
    setFewShotError(null);
  }, [initialData, agentConfigId, open]);

  const validateJson = (value: string, fieldName: string): boolean => {
    if (!value || value.trim() === '') return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleContextVarsChange = (value: string) => {
    setFormData({ ...formData, contextVariables: value });
    if (value && value.trim() !== '') {
      try {
        JSON.parse(value);
        setContextVarsError(null);
      } catch {
        setContextVarsError('JSON inválido');
      }
    } else {
      setContextVarsError(null);
    }
  };

  const handleFewShotChange = (value: string) => {
    setFormData({ ...formData, fewShotExamples: value });
    if (value && value.trim() !== '') {
      try {
        JSON.parse(value);
        setFewShotError(null);
      } catch {
        setFewShotError('JSON inválido');
      }
    } else {
      setFewShotError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateJson(formData.contextVariables, 'contextVariables')) {
      setContextVarsError('JSON inválido. Corrija el formato antes de guardar.');
      return;
    }
    if (!validateJson(formData.fewShotExamples, 'fewShotExamples')) {
      setFewShotError('JSON inválido. Corrija el formato antes de guardar.');
      return;
    }

    onSave(formData);
  };

  const handleFormatJson = (field: 'contextVariables' | 'fewShotExamples') => {
    const value = formData[field];
    if (!value || value.trim() === '') return;
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData({ ...formData, [field]: formatted });
      if (field === 'contextVariables') setContextVarsError(null);
      if (field === 'fewShotExamples') setFewShotError(null);
    } catch {
      if (field === 'contextVariables') setContextVarsError('JSON inválido. No se puede formatear.');
      if (field === 'fewShotExamples') setFewShotError('JSON inválido. No se puede formatear.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{mode === 'create' ? 'Nuevo Prompt' : 'Editar Prompt'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Dominio de Negocio"
              select
              value={formData.businessDomain}
              onChange={(e) => setFormData({ ...formData, businessDomain: e.target.value })}
              fullWidth
              required
            >
              {businessDomainOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Rol del Sistema"
              value={formData.systemRole}
              onChange={(e) => setFormData({ ...formData, systemRole: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              required
            />

            <TextField
              label="Tarea Principal"
              value={formData.mainTask}
              onChange={(e) => setFormData({ ...formData, mainTask: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              required
            />

            <TextField
              label="Instrucciones"
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
            />

            <TextField
              label="Restricciones"
              value={formData.restrictions}
              onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
            />

            <TextField
              label="Reglas de Formato de Respuesta"
              value={formData.responseFormatRules}
              onChange={(e) => setFormData({ ...formData, responseFormatRules: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
            />

            <TextField
              label="Entrada del Prompt"
              value={formData.promptInput}
              onChange={(e) => setFormData({ ...formData, promptInput: e.target.value })}
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
            />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Variables de Contexto (JSON)
                </Typography>
                <Button size="small" onClick={() => handleFormatJson('contextVariables')}>
                  Formatear JSON
                </Button>
              </Box>
              <TextField
                value={formData.contextVariables}
                onChange={(e) => handleContextVarsChange(e.target.value)}
                multiline
                minRows={6}
                maxRows={16}
                fullWidth
                placeholder='{ "key": "value" }'
                error={!!contextVarsError}
                helperText={contextVarsError}
                sx={jsonEditorSx}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Ejemplos Few-Shot (JSON)
                </Typography>
                <Button size="small" onClick={() => handleFormatJson('fewShotExamples')}>
                  Formatear JSON
                </Button>
              </Box>
              <TextField
                value={formData.fewShotExamples}
                onChange={(e) => handleFewShotChange(e.target.value)}
                multiline
                minRows={6}
                maxRows={16}
                fullWidth
                placeholder='[{ "input": "...", "output": "..." }]'
                error={!!fewShotError}
                helperText={fewShotError}
                sx={jsonEditorSx}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !!contextVarsError || !!fewShotError}
            startIcon={saving ? <CircularProgress size={20} /> : undefined}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateEditAgentPromptModal;
